import { FINANCING_FAQ_ANSWER, financingFaqParts } from "./financing-faq";

// Transparent pricing guide data. All figures are TYPICAL RANGES for
// Washington State (Puget Sound labor runs ~15-20% above national average).
// EDIT THESE NUMBERS FREELY — the owner approves final figures.
// Every range is presented on-site with a "final quote after on-site
// evaluation" disclaimer.

export const PRICING_DISCLAIMER =
  "Every home is different. The ranges below reflect typical, all-in pricing for common jobs in Western and Eastern Washington — your exact flat-rate quote is confirmed on site, before any work begins. No surprises, ever.";

export const PRICING = [
  {
    slug: "emergency-plumbing",
    name: "Emergency Plumbing (24/7)",
    range: "$150 – $500+",
    unit: "service call + first hour",
    note: "After-hours and weekend calls typically add $75–$150. We quote the full repair before starting.",
    includes: [
      "Rapid dispatch, day or night",
      "Diagnosis and immediate stabilization",
      "Upfront flat-rate quote for the repair",
    ],
  },
  {
    slug: "drain-cleaning-sewer",
    name: "Drain Cleaning & Sewer",
    range: "$250 – $900",
    unit: "per drain / main line",
    note: "Sink or tub drains usually run $250–$650; main sewer line snaking $350–$900. Hydro jetting for roots or heavy grease: $600–$1,500.",
    includes: [
      "Camera check where accessible",
      "Cable or hydro-jet clearing",
      "Flow test before we leave",
    ],
  },
  {
    slug: "water-heaters",
    name: "Water Heater Installation",
    range: "$1,600 – $3,200",
    unit: "standard tank, installed",
    note: "Tankless and heat-pump systems typically run $2,900–$5,200 installed. Code-required expansion tanks add $175–$225.",
    includes: [
      "Haul-away of the old unit",
      "Code-compliant fittings",
      "Full system test and walkthrough",
    ],
  },
  {
    slug: "repiping-pipe-repair",
    name: "Repiping & Pipe Repair",
    range: "$250 – $2,500",
    unit: "leak and pipe repairs",
    note: "Whole-home PEX repipes typically run $4,000–$15,000 depending on home size; copper systems higher. Free repipe evaluations.",
    includes: [
      "Exact leak location first",
      "Minimal-access wall openings",
      "Pressure test on completion",
    ],
  },
  {
    slug: "fixture-installation",
    name: "Fixtures & Faucets",
    range: "$250 – $800",
    unit: "per fixture, installed",
    note: "Faucet swaps usually $250–$650; toilet installs $350–$800 including the wax ring, supply line, and haul-away.",
    includes: [
      "Customer-supplied or sourced fixtures",
      "Shutoff valve check",
      "Leak-free guarantee",
    ],
  },
  {
    slug: "water-sewer-line-replacement",
    name: "Water & Sewer Lines",
    range: "$3,500 – $25,000+",
    unit: "full line replacement",
    note: "Water service lines typically $3,500–$12,000. Sewer replacements vary widely with depth, length, and trenchless vs. open-trench methods.",
    includes: [
      "Camera inspection & locate",
      "Trenchless options where possible",
      "Permits and inspections handled",
    ],
  },
  {
    slug: "gas-lines",
    name: "Gas Line Services",
    range: "$500 – $2,500",
    unit: "per run / hookup",
    note: "New appliance hookups sit at the lower end; longer runs, exterior work, and pressure testing add cost. Always permitted and inspected.",
    includes: [
      "Licensed gas piping work",
      "Pressure test & leak check",
      "Permit and inspection managed for you",
    ],
  },
  {
    slug: "commercial-new-construction",
    name: "Commercial / New Construction",
    range: "Custom quote",
    unit: "bid per project",
    note: "Tenant improvements, ground-up plumbing, and maintenance contracts are bid from plans or a site walk — usually within 2 business days.",
    includes: [
      "Plan review & takeoff",
      "Scheduled draws for larger jobs",
      "Dedicated project contact",
    ],
  },
];

export const COST_FACTORS = [
  {
    title: "Access & location",
    body: "A water heater in a garage costs less to replace than one in a crawlspace or attic. Tight access adds labor time.",
  },
  {
    title: "Age of your home",
    body: "Pre-1980s homes often have galvanized pipe, outdated shutoffs, or code gaps that need correcting while we're in there.",
  },
  {
    title: "Permits & code",
    body: "Most Washington plumbing work requires a permit ($100–$500 depending on scope). We pull and manage every permit for you.",
  },
  {
    title: "Materials you choose",
    body: "PEX vs. copper, standard vs. tankless, builder-grade vs. designer fixtures — we'll price the options side by side.",
  },
  {
    title: "Urgency",
    body: "Scheduled work is the best value. After-hours emergency response carries a modest surcharge — we'll always tell you before dispatch.",
  },
  {
    title: "Hidden damage",
    body: "If we open a wall and find rot or mold, we stop and re-quote before proceeding. You approve every change in writing.",
  },
];

// The estimator's question tree and every estimator number now live in
// src/data/pricing-config.json (seed) and the live, admin-edited copy served
// by GET /api/pricing-config — see src/lib/estimator-engine.js. PRICING above
// remains the /pricing page's own copy (owner reconciles separately).

export const PRICING_FAQS = [
  {
    q: "Are these prices exact?",
    a: "They're honest typical ranges for Washington homes, not teaser rates. Your exact price is confirmed with a written flat-rate quote after we see the job — and that quote is what you pay, even if the work takes longer than expected.",
  },
  {
    q: "Do you charge for estimates?",
    a: "Quotes for scheduled work like water heaters, repipes, and fixture installs are free. Diagnostic visits for active problems (leaks, no hot water, clogs) carry a $75–$150 trip/diagnostic fee, which we apply toward the repair if you proceed.",
  },
  {
    q: "Why is Washington plumbing more expensive than national averages?",
    a: "Licensed plumber labor in the Puget Sound region runs roughly 15–20% above the national average, and Washington's permitting and code standards are among the strictest in the country. That oversight protects you — and it's reflected in regional pricing.",
  },
  {
    q: "Is financing available?",
    a: FINANCING_FAQ_ANSWER,
    parts: financingFaqParts("pricing-faq"),
  },
  {
    q: "What if you find extra problems mid-job?",
    a: "Work stops and we walk you through it with photos. Nothing beyond the original quote happens without your written approval. No surprise line items on the final invoice.",
  },
];
